import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Collection } from './core';
import { createReactiveContext } from '../../../../../../../helpers/reactiveContext';
import { useEvent } from '../../../../../../../hooks/useEvent';
import {
  dateFormats,
  dateTime,
  formatDate,
} from '../../../../../shared/packages/dateTime';
import { stateKeys } from '../constants';

const ControllerContext = createContext();
const CollectionContext = createContext();

const {
  Provider,
  useSelector: useStateSelector,
  useMethods,
} = createReactiveContext();

export const useCollection = () => useContext(CollectionContext);
export const useController = () => useContext(ControllerContext);

export { useStateSelector };

export const StateProvider = ({ data, children, value, onChange }) => {
  const collection = useMemo(() => new Collection(data), [data]);

  return (
    <Provider>
      <CollectionContext.Provider value={collection}>
        <ControllerProvider
          collection={collection}
          value={value}
          onChange={onChange}
        >
          {children}
        </ControllerProvider>
      </CollectionContext.Provider>
    </Provider>
  );
};

const dateKey = stateKeys.date;
const dateStrKey = stateKeys.dateStr;
function ControllerProvider({ children, collection, value, onChange }) {
  const { setState, getValue } = useMethods();

  const changeRef = useRef(onChange);
  changeRef.current = onChange;

  useEffect(() => {
    if (!value) {
      const next = collection.getFirstDate();
      const nextStr = formatDate(next.date, dateFormats.ISO);

      setState({ [dateKey]: next, [dateStrKey]: nextStr });

      changeRef.current(nextStr);

      return;
    }

    if (value === getValue(dateStrKey)) {
      return;
    }

    const next = collection.getDate({
      dateTime: dateTime(value),
      dateStr: value,
    });

    setState({ [dateKey]: next, [dateStrKey]: value });
  }, [value, collection, setState, getValue]);

  const handleChange = useEvent((data) => {
    const isDateTime = dateTime.isMoment(data);

    const dateStr = isDateTime ? formatDate(data, dateFormats.ISO) : null;

    const next = isDateTime
      ? collection.getDate({ dateTime: data, dateStr })
      : collection.getValue(data);

    const nextStr = isDateTime
      ? dateStr
      : formatDate(next.date, dateFormats.ISO);

    setState({ [dateKey]: next, [dateStrKey]: nextStr });
    onChange(nextStr);
  });

  const controller = useState(() => ({ onChange: handleChange }))[0];

  return (
    <ControllerContext.Provider value={controller}>
      {children}
    </ControllerContext.Provider>
  );
}
